import axios from 'axios';
import API from "../config/API";

export const voucherService = {
    // Voucher Settings
    getSettings,
    updateSettings,
    
    // Voucher Catalog Management
    createVoucherCatalog,
    listVoucherCatalogs,
    getVoucherCatalog,
    updateVoucherCatalog,
    deleteVoucherCatalog,
    bulkUpdateVoucherCatalogs,
    
    // Voucher Instance Management
    listVoucherInstances,
    getVoucherInstance,
    getVoucherInstanceByCode,
    voidVoucherInstance,
    
    // Dashboard
    getDashboard,
    
    // Voucher Operations
    issueVoucher,
    redeemVoucher,
    updateVoucherRedemption,
    updateVoucherBalance,
    resendVoucherEmail
};

/**
 * Get voucher settings
 * @returns {Promise} Promise resolving to voucher settings
 */
function getSettings() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.get(API.VOUCHER_SETTINGS, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Update voucher settings
 * @param {Object} settings - Settings to update
 * @returns {Promise} Promise resolving to updated settings
 */
function updateSettings(settings) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_SETTINGS_UPDATE, settings, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Create a new voucher catalog entry
 * @param {Object} catalog - Voucher catalog data
 * @returns {Promise} Promise resolving to created voucher catalog
 */
function createVoucherCatalog(catalog) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Creating voucher catalog with payload:', JSON.stringify(catalog));
    
    return axios.post(API.VOUCHER_CREATE, catalog, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Voucher catalog creation response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Voucher catalog creation error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

/**
 * List voucher catalogs with optional filters
 * @param {Object} filters - Optional filters
 * @returns {Promise} Promise resolving to list of voucher catalogs
 */
function listVoucherCatalogs(filters = {}) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_LIST, filters, { headers: headers })
        .then(response => {
            console.log('List voucher catalogs raw response:', response.data);
            
            // Handle both response formats
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                // New API format
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            } else if (response.data && response.data.status === 1) {
                // Direct API format
                return {
                    data: {
                        statusCode: 1,
                        responseData: {
                            status: response.data.status,
                            message: response.data.message,
                            data: response.data.data
                        }
                    }
                };
            }
            return response;
        });
}

/**
 * List voucher instances with optional filters
 * @param {Object} filters - Optional filters
 * @returns {Promise} Promise resolving to list of voucher instances
 */
function listVoucherInstances(filters = {}) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log('Fetching voucher instances with filters:', filters);
    
    return axios.post(API.VOUCHER_SALES, filters, { headers: headers })
        .then(response => {
            console.log('Voucher instances response:', response.data);
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                console.log('Transforming voucher response data');
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            console.error('Error fetching voucher instances:', error);
            throw error;
        });
}

/**
 * Get a single voucher catalog by ID
 * @param {String} catalogId - Voucher catalog ID
 * @returns {Promise} Promise resolving to voucher catalog details
 */
function getVoucherCatalog(catalogId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_VIEW, { catalogId }, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Get a single voucher instance by ID
 * @param {String} voucherId - Voucher instance ID
 * @returns {Promise} Promise resolving to voucher instance details
 */
function getVoucherInstance(voucherId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_VIEW, { voucherId }, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Update a voucher catalog
 * @param {Object} catalog - Voucher catalog data with catalogId
 * @returns {Promise} Promise resolving to updated voucher catalog
 */
function updateVoucherCatalog(catalog) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_UPDATE, catalog, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Delete a voucher catalog
 * @param {String} catalogId - Voucher catalog ID
 * @returns {Promise} Promise resolving to success message
 */
function deleteVoucherCatalog(catalogId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_DELETE, { catalogId }, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Void a voucher instance
 * @param {String} voucherId - Voucher instance ID
 * @returns {Promise} Promise resolving to success message
 */
function voidVoucherInstance(voucherId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_VOID, { voucherId }, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Get dashboard data
 * @returns {Promise} Promise resolving to dashboard data
 */
function getDashboard() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.get(API.VOUCHER_DASHBOARD, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        });
}

/**
 * Bulk update voucher catalogs and settings
 * @param {Object} data - Object containing settings, vouchersToCreate, vouchersToUpdate, and vouchersToDelete
 * @returns {Promise} Promise resolving to bulk update result
 */
function bulkUpdateVoucherCatalogs(data) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Bulk updating voucher catalogs with payload:', JSON.stringify(data));
    
    return axios.post(API.VOUCHER_BULK_UPDATE, data, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Bulk update response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Bulk update error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

/**
 * Get a voucher instance by code
 * @param {String} code - Voucher code
 * @returns {Promise} Promise resolving to voucher instance details
 */
function getVoucherInstanceByCode(code) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.VOUCHER_VIEW, { code }, { headers: headers })
        .then(response => {
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        statusCode: response.data.statusCode,
                        responseData: response.data.responseData
                    }
                };
            }
            return response;
        })
        .catch(error => {
            console.error('Error fetching voucher by code:', error);
            throw error;
        });
}

/**
 * Issue a new voucher
 * @param {Object} params - Parameters for issuing a voucher
 * @returns {Promise} Promise resolving to issued voucher
 */
function issueVoucher(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Issuing voucher with payload:', JSON.stringify(params));
    
    return axios.post(API.VOUCHER_ISSUE, params, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Voucher issue response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Voucher issue error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

/**
 * Redeem a voucher
 * @param {Object} params - Parameters for redeeming a voucher
 * @returns {Promise} Promise resolving to redemption result
 */
function redeemVoucher(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Redeeming voucher with payload:', JSON.stringify(params));
    
    return axios.post(API.VOUCHER_REDEEM, params, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Voucher redeem response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Voucher redeem error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

/**
 * Update a voucher redemption with a booking ID
 * @param {String} voucherId - Voucher ID
 * @param {Number} redemptionIndex - Index of the redemption to update
 * @param {String} bookingId - Booking ID to associate with the redemption
 * @returns {Promise} Promise resolving to updated voucher
 */
function updateVoucherRedemption(voucherId, redemptionIndex, bookingId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Updating voucher redemption with params:', { voucherId, redemptionIndex, bookingId });
    
    return axios.post(API.VOUCHER_UPDATE_REDEMPTION, { voucherId, redemptionIndex, bookingId }, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Voucher redemption update response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Voucher redemption update error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

                    /**
                     * Update a voucher's balance
                     * @param {String} voucherId - Voucher ID
 * @param {Number} remainingValue - New remaining value for the voucher
 * @returns {Promise} Promise resolving to updated voucher
 */
function updateVoucherBalance(voucherId, remainingValue) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Updating voucher balance with params:', { voucherId, remainingValue });
    
    return axios.post(API.VOUCHER_UPDATE_BALANCE, { voucherId, remainingValue }, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Voucher balance update response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        statusCode: response.data.statusCode,
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Voucher balance update error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

/**
 * Resend voucher email
 * @param {String} voucherId - Voucher instance ID
 * @returns {Promise} Promise resolving to success message
 */
function resendVoucherEmail(voucherId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // Log the request for debugging
    console.log('Resending voucher email for voucherId:', voucherId);
    
    return axios.post(API.VOUCHER_RESEND_EMAIL, { voucherId }, { headers: headers })
        .then(response => {
            // Log the response for debugging
            console.log('Resend email response:', JSON.stringify(response.data));
            
            if (response.data && response.data.statusCode === 1 && response.data.responseData) {
                return {
                    data: {
                        status: response.data.responseData.status,
                        message: response.data.responseData.message,
                        data: response.data.responseData.data
                    }
                };
            }
            return response;
        })
        .catch(error => {
            // Log the error for debugging
            console.error('Resend email error:', error);
            console.error('Error response:', error.response ? JSON.stringify(error.response.data) : 'No response data');
            throw error;
        });
}

// Add resendVoucherEmail to the exported service
voucherService.resendVoucherEmail = resendVoucherEmail;